import React from 'react';

import { BlockType } from 'api/cms';
import { getNextRealBlock, getPrevRealBlock } from 'cms/helpers';
import { MappedContentBlock } from 'cms/mappers';
import { Box } from 'components/Box';
import Divider from 'components/Divider';

import { ContentPageBlockContainer } from './ContentPageBlockContainer';

type Props = {
  mappedBlocks: MappedContentBlock[];
  withSidebar?: boolean;
  fullWidthLayout?: boolean;
};

const blocksWithoutHeadingDivider: BlockType[] = [
  'GRAD_CTA_HERO',
  'CTA_HERO',
  'EMAIL_CAMPAIGN_SIGNUP_SINGLE',
  'EMAIL_CAMPAIGN_SIGNUP_DOUBLE',
  'FORM_BUILDER',
  'TWO_TILES',
  'SPLIT_CONTENT',
  'FLASH_CARD_QUIZ',
  'PERSONA_QUIZ',
  'TILE_GRID',
  'SIGN_UP',
  'SCROLL_TRACKING',
];

const canHaveHeadingDivider = ({ type }) =>
  !blocksWithoutHeadingDivider.some((blockType) => type === blockType);

const blocksWithDividers: BlockType[] = [
  'RANDOM_GRAD_PROGRAMS',
  'RECOMMENDED_ACTIONS',
  'THREE_SECTION_SLIDER',
  'FOUR_ARTICLES_HIGHLIGHT',
  'THREE_STEP_CTA',
];

const mustHaveDivider = ({ type }) => blocksWithDividers.includes(type);

const blocksWithoutDividers: BlockType[] = [
  'GRAD_CTA_HERO',
  'CTA_HERO',
  'STICKY_HEADER_LINKS',
  'STICKY_HEADER_LINKS_GRAD',
  'EMAIL_CAMPAIGN_SIGNUP_SINGLE',
  'EMAIL_CAMPAIGN_SIGNUP_DOUBLE',
  'FORM_BUILDER',
  'FLASH_CARD_QUIZ',
  'SIGN_UP',
  'SCROLL_TRACKING',
];

const mustNotHaveDivider = ({ type, mustNotHaveDivider: noDivider }) =>
  blocksWithoutDividers.includes(type) || noDivider;

export function ContentPageBlocksWithContainer({
  mappedBlocks,
  withSidebar,
  fullWidthLayout,
}: Props) {
  return (
    <>
      {mappedBlocks.map((block, index) => {
        const prevBlock = getPrevRealBlock(mappedBlocks, index);
        const nextBlock = getNextRealBlock(mappedBlocks, index);

        if (block.type === 'ANCHOR') {
          return null;
        }

        return (
          <ContentPageBlockContainer
            block={block}
            blocks={mappedBlocks}
            index={index}
            id={`cms-block-${block.id}`}
            key={block.id}
            fullWidthLayout={fullWidthLayout}
            withSidebar={withSidebar}
            showHeadingDivider={fullWidthLayout && canHaveHeadingDivider(block)}
            hideFirstHeadingDivider={
              index === 0 ||
              (index > 0 &&
                prevBlock &&
                (mustHaveDivider(prevBlock) ||
                  mustHaveDivider(block) ||
                  mustNotHaveDivider(prevBlock) ||
                  prevBlock.hasBackground))
            }
          >
            {fullWidthLayout &&
              index > 0 &&
              mustHaveDivider(block) &&
              prevBlock &&
              !mustNotHaveDivider(prevBlock) &&
              !block.hasBackground && (
                <Box
                  maxWidth={1200}
                  mb={
                    fullWidthLayout
                      ? [64, null, null, 70]
                      : [40, null, null, 60]
                  }
                  mx="auto"
                >
                  <Divider light my={0} />
                </Box>
              )}
            {block.component}
            {fullWidthLayout &&
              index <= mappedBlocks.length - 2 &&
              mustHaveDivider(block) &&
              nextBlock &&
              !mustHaveDivider(nextBlock) &&
              !mustNotHaveDivider(nextBlock) &&
              ((!block.hasBackground && !nextBlock.hasBackground) ||
                (block.hasBackground && nextBlock.hasBackground)) && (
                <Box
                  maxWidth={1200}
                  mt={
                    fullWidthLayout
                      ? [64, null, null, 70]
                      : [40, null, null, 60]
                  }
                  mx="auto"
                >
                  <Divider light my={0} />
                </Box>
              )}
          </ContentPageBlockContainer>
        );
      })}
    </>
  );
}
